// MEGA-SPRINT Sprint 4 PR 4.0 — dual-identity team_member display resolver.
//
// Analogous to `ballHolderDisplay.ts` but for `team_members` rows. The
// `team_members` table already supports dual identity via `user_id`
// (FK → profiles) XOR `customer_id` (FK → customers). The application
// code historically used `user_id`-only patterns + `.not("user_id", "is", null)`
// filters that silently excluded customer-only members from display surfaces.
//
// This module provides:
//
//   1. `resolveTeamMember(row): ResolvedTeamMember` — discriminated result
//      with `{ kind: 'user' | 'customer'; id; displayName }`. Pure synchronous
//      function operating on FK-embedded data.
//
//   2. `pickFirstTwoPrimaryIdentities(members)` — variant of existing
//      `pickFirstTwoPrimaries` that returns the identity discriminator for
//      each primary slot. Use when the caller needs to know KIND of each slot
//      (e.g. to decide which downstream FK column to write, or which email
//      source-of-truth to use for notifications).
//
// Display name fallback chain (matches `pickFirstTwoPrimaryNames`):
//   - kind='user'     → profile.full_name?.trim() || unknownLabel
//   - kind='customer' → customer.full_name?.trim() || customer.email?.trim() || unknownLabel
//
// Mutex invariant: `team_members` has CHECK constraint enforcing
// num_nonnulls(user_id, customer_id) = 1, so exactly one of the two is set
// per row at the DB layer. The resolver defensively prefers `user_id` if a
// caller constructs an invalid in-memory fixture with both set.

import type { TeamMemberRole } from "./teamHelpers";
import { getTeamPrimaryPlayers } from "./teamHelpers";

/** Display fallback when full_name/email is null/blank or the embed is missing. */
const UNKNOWN_DISPLAY = "Spelare";

/**
 * Discriminated team-member display result. The `kind` field tells the caller
 * which identity path carried the member. `displayName` is always non-empty.
 * Used by display surfaces (MatchDetail, StandingsTable, MySchedule, etc.)
 * to render either-kind without re-checking which FK is populated.
 */
export type ResolvedTeamMember =
  | { kind: "user"; userId: string; displayName: string }
  | { kind: "customer"; customerId: string; displayName: string }
  | { kind: "none"; displayName: string };

/**
 * Shape expected by `resolveTeamMember`. Callers must FK-embed both the
 * profile and customer joins when fetching team_members:
 *
 *   members:team_members(
 *     user_id, customer_id, role, joined_at,
 *     profile:profiles!team_members_user_id_fkey(full_name),
 *     customer:customers!team_members_customer_id_fkey(full_name, email)
 *   )
 *
 * The `?` markers tolerate callers who only need one side (e.g. legacy code
 * that doesn't yet embed customer:).
 */
export interface TeamMemberEmbed {
  user_id: string | null;
  customer_id: string | null;
  profile?: { full_name: string | null } | null;
  customer?: { full_name: string | null; email: string | null } | null;
}

/**
 * Resolve a single team_members row to a discriminated display result.
 * Synchronous + pure. Works with FK-embedded data from Supabase `.select()`.
 *
 * Returns:
 *   - `{ kind: 'user', userId, displayName }` when user_id is set
 *   - `{ kind: 'customer', customerId, displayName }` when customer_id is set
 *   - `{ kind: 'none', displayName: 'Spelare' }` when both NULL (defensive;
 *     CHECK guarantees this doesn't happen at DB layer)
 *
 * `displayName` always non-empty. Falls back to `'Spelare'` if no resolvable
 * name (profile + customer.full_name + customer.email all empty/null).
 */
export function resolveTeamMember(row: TeamMemberEmbed): ResolvedTeamMember {
  if (row.user_id) {
    const displayName = row.profile?.full_name?.trim() || UNKNOWN_DISPLAY;
    return { kind: "user", userId: row.user_id, displayName };
  }
  if (row.customer_id) {
    const displayName =
      row.customer?.full_name?.trim() ||
      row.customer?.email?.trim() ||
      UNKNOWN_DISPLAY;
    return { kind: "customer", customerId: row.customer_id, displayName };
  }
  return { kind: "none", displayName: UNKNOWN_DISPLAY };
}

/**
 * Pick the first two primary team members and return their identity-discriminated
 * resolution. Variant of `pickFirstTwoPrimaries` from `teamHelpers.ts` that gives
 * callers the `{ kind, id, displayName }` triple per slot so they can
 *   - render either kind without null-checking FK columns
 *   - decide which downstream FK to write (e.g. ball_holder picker)
 *   - decide notification email source (profile.email vs customers.email)
 *
 * Order matches `pickFirstTwoPrimaries`: role='primary' filtered + sorted by
 * `joined_at` ASC. Returns `[null, null]` for empty/missing rosters.
 */
export function pickFirstTwoPrimaryIdentities<
  M extends TeamMemberEmbed & { role: TeamMemberRole; joined_at: string },
>(
  members: M[] | null | undefined,
): [ResolvedTeamMember | null, ResolvedTeamMember | null] {
  if (!members || members.length === 0) return [null, null];
  const primaries = getTeamPrimaryPlayers(members);
  const first = primaries[0] ? resolveTeamMember(primaries[0]) : null;
  const second = primaries[1] ? resolveTeamMember(primaries[1]) : null;
  return [first, second];
}

/**
 * Resolve all primary members in order (not just first two). Used by
 * leaguePlayerHelpers / LeagueCSVExport / standings displays that need the
 * complete roster. Order: role='primary' + joined_at ASC.
 */
export function resolveAllPrimaryIdentities<
  M extends TeamMemberEmbed & { role: TeamMemberRole; joined_at: string },
>(members: M[] | null | undefined): ResolvedTeamMember[] {
  if (!members || members.length === 0) return [];
  return getTeamPrimaryPlayers(members).map(resolveTeamMember);
}
